import { useEffect, useState } from "react";
import { Button, DatePicker, Input, Segmented, Select, Tag } from "antd";
import { EnterOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { ActivityControl, ActivityField } from "./activity-field";
import "./activity-modal.css";

export const DEFAULT_PERIODIC_RECURRENCE = {
  type: "periodic",
  repeatMode: "interval",
  interval: 1,
  unit: "days",
  repeatOn: [],
  endType: "never",
  endDate: null,
  occurrenceCount: null,
};

export const WEEKDAY_OPTIONS = [
  { label: "Su", title: "Sunday", value: 0 },
  { label: "Mo", title: "Monday", value: 1 },
  { label: "Tu", title: "Tuesday", value: 2 },
  { label: "We", title: "Wednesday", value: 3 },
  { label: "Th", title: "Thursday", value: 4 },
  { label: "Fr", title: "Friday", value: 5 },
  { label: "Sa", title: "Saturday", value: 6 },
];

/**
 * An older activity may hold `repeatMode: "weekdays"`, which meant "every week on
 * these days". It is read as an interval of one week, which is the same thing
 * said the way the rest of this understands it.
 */
export function normalizePeriodicDraft(periodicRecurrence) {
  if (!periodicRecurrence) {
    return DEFAULT_PERIODIC_RECURRENCE;
  }

  if (periodicRecurrence.repeatMode === "weekdays") {
    return {
      ...DEFAULT_PERIODIC_RECURRENCE,
      ...periodicRecurrence,
      repeatMode: "interval",
      interval: 1,
      unit: "weeks",
      repeatOn: Array.isArray(periodicRecurrence.repeatOn)
        ? periodicRecurrence.repeatOn
        : [],
    };
  }

  return {
    ...DEFAULT_PERIODIC_RECURRENCE,
    ...periodicRecurrence,
    repeatMode: "interval",
    repeatOn:
      periodicRecurrence.unit === "weeks" && Array.isArray(periodicRecurrence.repeatOn)
        ? periodicRecurrence.repeatOn
        : [],
  };
}

// The shape the activity is saved in — the same object the modal used to build
// when Save was pressed.
const toPayload = (draft) => ({
  type: "periodic",
  repeatMode: "interval",
  interval: Number(draft.interval),
  unit: draft.unit,
  repeatOn:
    draft.unit === "weeks"
      ? [...new Set(draft.repeatOn || [])].sort((a, b) => a - b)
      : [],
  endType: draft.endType,
  endDate: draft.endType === "on" ? draft.endDate : null,
  occurrenceCount:
    draft.endType === "after" ? Number(draft.occurrenceCount) : null,
});

// Unchanged from the modal this replaces, down to the wording.
function validate(draft) {
  const errors = {};
  const interval = Number(draft.interval);
  const occurrenceCount = Number(draft.occurrenceCount);

  if (!Number.isInteger(interval) || interval <= 0) {
    errors.interval = "Interval must be greater than 0";
  }

  if (draft.endType === "on") {
    if (!draft.endDate) {
      errors.endDate = "End date is required";
    } else if (dayjs(draft.endDate).startOf("day").isBefore(dayjs().startOf("day"))) {
      errors.endDate = "End date cannot be in the past";
    }
  }

  if (
    draft.endType === "after" &&
    (!Number.isInteger(occurrenceCount) || occurrenceCount <= 0)
  ) {
    errors.occurrenceCount = "Occurrence count must be greater than 0";
  }

  return errors;
}

/**
 * How the recurrence reads on the plan itself, on the activity's own chip. Not
 * used by the panel — the fields there say it in full — but it belongs with the
 * rules rather than beside the chip that prints it.
 */
export function getPeriodicRecurrenceSummary(periodicRecurrence) {
  if (!periodicRecurrence) {
    return "";
  }

  const legacyWeekdayMode = periodicRecurrence.repeatMode === "weekdays";
  const interval = legacyWeekdayMode ? 1 : Number(periodicRecurrence.interval || 1);
  const unit = periodicRecurrence.unit === "weeks" || legacyWeekdayMode ? "week" : "day";
  const unitLabel = interval === 1 ? unit : `${unit}s`;
  const selected = Array.isArray(periodicRecurrence.repeatOn)
    ? periodicRecurrence.repeatOn
    : [];
  const labels = WEEKDAY_OPTIONS
    .filter((day) => selected.includes(day.value))
    .map((day) => day.title.slice(0, 3));

  return `Repeats every ${interval} ${unitLabel}${
    unit === "week" && labels.length ? ` on ${labels.join(", ")}` : ""
  }`;
}

/**
 * LF360-1881 — when a triggered task comes round, in the panel rather than in a
 * modal on top of it.
 *
 * The two shapes are the two the activity has always had, and they are still the
 * same pair of mutually exclusive columns: `offset`, a list of days from the
 * baseline, or `periodicRecurrence`. Choosing one clears the other, which the
 * two callbacks below already do — this only decides which of them is called.
 *
 * The design draws the fields open on the panel, so there is no Save to press.
 * The draft is still held here and only handed over once it is valid: the same
 * three rules the modal enforced, minus the trip through a dialog. What is
 * invalid stays local and says so, rather than being written and refused.
 * Numbers commit when they are finished with — a save per keystroke would send
 * "3" on the way to "30".
 */
const RecurrenceField = ({
  periodicRecurrence = null,
  offsets = [],
  onChangeRecurrence,
  onChangeOffsets,
  readOnly = false,
  getPopupContainer,
}) => {
  const isPeriodic = !!periodicRecurrence;

  const [draft, setDraft] = useState(() => normalizePeriodicDraft(periodicRecurrence));
  const [errors, setErrors] = useState({});
  const [newOffset, setNewOffset] = useState("");
  const [isAddingOffset, setIsAddingOffset] = useState(false);

  useEffect(() => {
    setDraft(normalizePeriodicDraft(periodicRecurrence));
    setErrors({});
  }, [periodicRecurrence]);

  const sortedOffsets = [...offsets].sort((a, b) => a - b);

  // One way in, so no field can commit without being checked.
  const change = (changes) => {
    if (readOnly) return;

    const next = { ...draft, ...changes };
    const found = validate(next);

    setDraft(next);
    setErrors(found);

    if (!Object.keys(found).length) {
      onChangeRecurrence?.(toPayload(next));
    }
  };

  const setMode = (mode) => {
    if (readOnly) return;

    if (mode === "none") {
      // Clears the recurrence and gives the activity its offsets back — the same
      // call the old select made, and the only one that leaves the pair
      // consistent.
      onChangeRecurrence?.(null);
      return;
    }

    const next = normalizePeriodicDraft(periodicRecurrence);
    setDraft(next);
    setErrors({});
    onChangeRecurrence?.(toPayload(next));
  };

  const addOffset = () => {
    if (readOnly) return;

    const value = parseInt(newOffset, 10);

    if (Number.isNaN(value) || value < 0 || offsets.includes(value)) {
      return;
    }

    onChangeOffsets?.([...offsets, value]);
    setNewOffset("");
    setIsAddingOffset(false);
  };

  const removeOffset = (value) => {
    if (readOnly) return;
    onChangeOffsets?.(offsets.filter((offset) => offset !== value));
  };

  const isValidOffset =
    !Number.isNaN(parseInt(newOffset, 10)) && parseInt(newOffset, 10) >= 0;

  return (
    <>
      <ActivityField label="Recurrence">
        <Segmented
          block
          className="activity-segmented lf-segmented"
          disabled={readOnly}
          value={isPeriodic ? "periodic" : "none"}
          onChange={setMode}
          options={[
            { label: "Repeating", value: "periodic" },
            { label: "Custom", value: "none" },
          ]}
        />
      </ActivityField>

      {isPeriodic ? (
        <>
          <ActivityField label="Every">
            <div className="activity-field__row">
              <ActivityControl className="activity-duration">
                <input
                  className="activity-duration__value"
                  type="text"
                  inputMode="numeric"
                  aria-label="Every"
                  value={draft.interval ?? ""}
                  disabled={readOnly}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      interval: event.target.value.replace(/[^\d]/g, "").slice(0, 3),
                    }))
                  }
                  onBlur={() => change({ interval: Number(draft.interval) })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      change({ interval: Number(draft.interval) });
                    }
                  }}
                />
              </ActivityControl>

              <ActivityControl>
                <Select
                  aria-label="Repeat unit"
                  value={draft.unit}
                  variant="borderless"
                  disabled={readOnly}
                  onChange={(unit) =>
                    change({ unit, repeatOn: unit === "weeks" ? draft.repeatOn || [] : [] })
                  }
                  options={[
                    { label: "Day", value: "days" },
                    { label: "Week", value: "weeks" },
                  ]}
                  getPopupContainer={getPopupContainer}
                />
              </ActivityControl>
            </div>
            {errors.interval && (
              <p className="activity-field__error">{errors.interval}</p>
            )}
          </ActivityField>

          {draft.unit === "weeks" && (
            <ActivityField label="Repeat on">
              <div className="activity-weekdays">
                {WEEKDAY_OPTIONS.map((day) => {
                  const selected = (draft.repeatOn || []).includes(day.value);

                  return (
                    <button
                      key={day.value}
                      type="button"
                      className={
                        selected
                          ? "activity-weekday activity-weekday--selected"
                          : "activity-weekday"
                      }
                      // The square says "Mo"; a screen reader should say Monday.
                      title={day.title}
                      aria-label={day.title}
                      aria-pressed={selected}
                      disabled={readOnly}
                      onClick={() => {
                        const current = draft.repeatOn || [];
                        const repeatOn = current.includes(day.value)
                          ? current.filter((value) => value !== day.value)
                          : [...current, day.value].sort((a, b) => a - b);

                        change({ repeatOn });
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </ActivityField>
          )}

          <ActivityField label="Ends">
            <ActivityControl>
              <Select
                aria-label="Ends"
                value={draft.endType}
                variant="borderless"
                disabled={readOnly}
                onChange={(endType) => change({ endType })}
                options={[
                  { label: "Never", value: "never" },
                  { label: "On", value: "on" },
                  { label: "After", value: "after" },
                ]}
                getPopupContainer={getPopupContainer}
              />
            </ActivityControl>
          </ActivityField>

          {draft.endType === "on" && (
            <ActivityField label="End date">
              <ActivityControl>
                <DatePicker
                  aria-label="End date"
                  variant="borderless"
                  disabled={readOnly}
                  value={draft.endDate ? dayjs(draft.endDate) : null}
                  onChange={(value) =>
                    change({ endDate: value ? value.format("YYYY-MM-DD") : null })
                  }
                  disabledDate={(current) =>
                    current && current.startOf("day").isBefore(dayjs().startOf("day"))
                  }
                  getPopupContainer={getPopupContainer}
                />
              </ActivityControl>
              {errors.endDate && (
                <p className="activity-field__error">{errors.endDate}</p>
              )}
            </ActivityField>
          )}

          {draft.endType === "after" && (
            <ActivityField label="Occurrences">
              <ActivityControl className="activity-duration">
                <input
                  className="activity-duration__value"
                  type="text"
                  inputMode="numeric"
                  aria-label="Occurrences"
                  value={draft.occurrenceCount ?? ""}
                  disabled={readOnly}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      occurrenceCount: event.target.value.replace(/[^\d]/g, "").slice(0, 4),
                    }))
                  }
                  onBlur={() => change({ occurrenceCount: Number(draft.occurrenceCount) })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      change({ occurrenceCount: Number(draft.occurrenceCount) });
                    }
                  }}
                />
              </ActivityControl>
              {errors.occurrenceCount && (
                <p className="activity-field__error">{errors.occurrenceCount}</p>
              )}
            </ActivityField>
          )}
        </>
      ) : (
        <ActivityField label="Offset, day(s)">
          <div className="activity-chips">
            {sortedOffsets.map((offset) => (
              <Tag
                key={offset}
                className="activity-chip"
                closable={!readOnly}
                onClose={() => removeOffset(offset)}
              >
                {offset === 1 ? "1 day" : `${offset} days`}
              </Tag>
            ))}

            {!readOnly &&
              (isAddingOffset ? (
                <span className="activity-chip-input">
                  <Input
                    value={newOffset}
                    autoFocus
                    aria-label="Offset in days"
                    style={{ width: 84 }}
                    onChange={(event) => setNewOffset(event.target.value)}
                    onPressEnter={addOffset}
                  />
                  <Button
                    type="text"
                    aria-label="Add offset"
                    disabled={!isValidOffset}
                    onClick={addOffset}
                    icon={<EnterOutlined />}
                  />
                </span>
              ) : (
                <Button
                  className="activity-chip-add"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAddingOffset(true)}
                >
                  Add
                </Button>
              ))}
          </div>
        </ActivityField>
      )}
    </>
  );
};

export default RecurrenceField;
